import { useContext, useEffect, useMemo, useState } from 'react';

import UserContext from 'context/user.context';
import Card from 'components/Card';
import useBaseURL from 'hooks/useBaseURL';
import { safeJoinURL } from 'libs/url';
import clsx from 'libs/clsx';
import Typography from 'components/Typography';
import ExamControllerContext from 'context/examController.context';
import encryptSimulasiToken from 'utils/simulasiToken';

const FOTO_THUMB_PATH = process.env.REACT_APP_FOTO_THUMB_PATH;

function ProfileCard() {
  const [user] = useContext(UserContext);
  const [{ isSimulation }] = useContext(ExamControllerContext);
  const baseURL = useBaseURL();
  const [simulationProfile] = useState(null);
  const [profilePhotoURL, setProfilePhotoURL] = useState(null);
  const lastLoginUsername = localStorage.getItem('lastLoginUsername');

  useEffect(() => {
    let isMounted = true;

    async function setupProfilePhoto() {
      if (!baseURL.backend) {
        if (isMounted) setProfilePhotoURL(null);
        return;
      }

      const nisn = isSimulation ? simulationProfile?.nisn : user.nisn;

      if (!nisn) {
        if (isMounted) setProfilePhotoURL(null);
        return;
      }

      try {
        const token = await encryptSimulasiToken(nisn);
        const nextPhotoURL = `${safeJoinURL(
          FOTO_THUMB_PATH
        )}?token=${encodeURIComponent(token)}`;
        if (isMounted) {
          setProfilePhotoURL(nextPhotoURL);
        }
      } catch (error) {
        if (isMounted) {
          setProfilePhotoURL(null);
        }
      }
    }

    setupProfilePhoto();

    return () => {
      isMounted = false;
    };
  }, [baseURL.backend, isSimulation, simulationProfile, user.nisn]);

  const profile = useMemo(
    () => (isSimulation ? simulationProfile : user),
    [isSimulation, simulationProfile, user]
  );
  const profileName = profile?.nama || 'Simulasi';
  const profileCode = isSimulation
    ? profile?.noUjian || lastLoginUsername || 'Simulasi'
    : profile?.kdr;
  const profileLocation = isSimulation
    ? profile?.ptkin || 'Simulasi'
    : profile?.ptk_nmp;

  return (
    <>
      <Card className="px-0 py-0 md:px-6 md:py-8">
        <div className="flex items-center gap-3 px-4 md:px-0 pt-1 md:gap-5 md:pt-5">
          {profilePhotoURL && (
            <img
              src={profilePhotoURL}
              alt="Foto profil"
              className="w-20 h-24 md:w-20 md:h-24 rounded-xl md:rounded-xl object-cover shrink-0"
            />
          )}
          <div className="min-w-0">
            <Typography
              className={clsx(
                'font-medium text-lg leading-tight md:text-base',
                isSimulation && 'uppercase'
              )}
            >
              {profileName}
            </Typography>
            <Typography
              variant="small"
              className="text-gray-400 font-medium text-base md:text-sm mt-1 md:mt-1.5"
            >
              {profileCode}
            </Typography>
          </div>
        </div>

        <div className="grid grid-flow-col grid-cols-1 gap-1 md:gap-3 mt-3 md:mt-6 px-4 md:px-0">
          <div>
            <Typography
              variant="small"
              className="font-normal text-gray-400 text-sm md:text-sm"
              gutterBottom
            >
              Titik Lokasi:
            </Typography>
            <Typography className="font-normal text-base md:text-base leading-tight">
              {profileLocation}
            </Typography>
          </div>
        </div>
      </Card>
    </>
  );
}

export default ProfileCard;
